import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generate a PDF with QR codes arranged in 4×4 grid layout
 * Each page contains 16 QR codes (4 columns × 4 rows)
 * Additional pages are created as needed for larger batches
 *
 * @param {Array} bulkQRTokens - Array of QR token objects with device information
 * @param {React.MutableRefObject} qrCanvasRefs - Refs to QR canvas elements
 * @returns {Promise<void>}
 */
export const generateQRPDF = async (bulkQRTokens, qrCanvasRefs) => {
  if (!bulkQRTokens || bulkQRTokens.length === 0) {
    throw new Error('No QR codes to generate PDF');
  }

  // A4 page dimensions in mm
  const pageWidth = 210;
  const pageHeight = 297;

  // Margins
  const marginTop = 10;
  const marginLeft = 10;
  const marginRight = 10;
  const marginBottom = 10;

  // Calculate available space
  const availableWidth = pageWidth - marginLeft - marginRight;
  const availableHeight = pageHeight - marginTop - marginBottom;

  // Grid: 2 columns x 2 rows
  const cols = 2;
  const rows = 2;
  const cellsPerPage = cols * rows;

  // Calculate cell dimensions (with spacing)
  const spacing = 5; // mm between cells
  const cellWidth = (availableWidth - spacing) / cols;
  const cellHeight = availableHeight / rows;

  // QR code size (smaller than cell to leave room for device info)
  const qrSize = 50; // mm

  // Create PDF (A4, landscape-friendly portrait layout)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'A4'
  });

  // Calculate total pages needed
  const totalPages = Math.ceil(bulkQRTokens.length / cellsPerPage);

  // Process each page
  for (let pageNum = 0; pageNum < totalPages; pageNum++) {
    // Add new page (except for first page, which is created by default)
    if (pageNum > 0) {
      pdf.addPage();
    }

    // Get QR codes for this page
    const pageStartIndex = pageNum * cellsPerPage;
    const pageEndIndex = Math.min(pageStartIndex + cellsPerPage, bulkQRTokens.length);
    const pageQRs = bulkQRTokens.slice(pageStartIndex, pageEndIndex);

    // Add page header
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'bold');
    pdf.text(`Device QR Labels - Page ${pageNum + 1} of ${totalPages}`, pageWidth / 2, 5, { align: 'center' });

    // Add page number at bottom
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'normal');
    pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });

    // Add QR codes in 4x4 grid (16 per page)
    for (let idx = 0; idx < pageQRs.length; idx++) {
      const qr = pageQRs[idx];

      // Calculate grid position
      const row = Math.floor(idx / cols);
      const col = idx % cols;

      const x = marginLeft + (col * (cellWidth + spacing));
      const y = marginTop + (row * cellHeight);

      // Draw cell background
      pdf.setDrawColor(220, 220, 220);
      pdf.setFillColor(255, 255, 255);
      pdf.rect(x, y, cellWidth, cellHeight, 'FD');

      // Get QR canvas from refs
      const qrCanvasRef = qrCanvasRefs[pageStartIndex + idx];

      if (qrCanvasRef?.current) {
        try {
          // Convert QR canvas to image
          const canvas = qrCanvasRef.current;
          const imgData = canvas.toDataURL('image/png');

          // Calculate centered position for QR code
          const qrX = x + (cellWidth - qrSize) / 2;
          const qrY = y + 10;

          // Add QR code image to PDF
          pdf.addImage(imgData, 'PNG', qrX, qrY, qrSize, qrSize);
        } catch (error) {
          console.error(`Error adding QR code ${qr.device_identifier} to PDF:`, error);
        }
      }

      // Add device information below QR code
      const infoY = y + qrSize + 12;

      // Device name
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      const deviceName = qr.device_name || 'Unknown Device';
      const deviceNameTruncated = deviceName.length > 20 ? deviceName.substring(0, 17) + '...' : deviceName;
      pdf.text(deviceNameTruncated, x + cellWidth / 2, infoY, { align: 'center', maxWidth: cellWidth - 2 });

      // Device ID
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      const idTruncated = qr.device_identifier.length > 25 ? qr.device_identifier.substring(0, 22) + '...' : qr.device_identifier;
      pdf.text(`ID: ${idTruncated}`, x + cellWidth / 2, infoY + 5, { align: 'center', maxWidth: cellWidth - 2 });
    }
  }

  // Generate filename with timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `device-qr-codes-${timestamp}.pdf`;

  // Download PDF
  pdf.save(filename);
};

/**
 * Capture currently rendered `.qr-batch-card` nodes from QRBatchRenderer.
 * Call after a chunk has been rendered (keep chunks ~20 so capture stays fast).
 */
export const captureQRBatchCards = async ({
  expectedCount,
  targetDPI = 200,
  isCancelled = null,
  onCardCaptured = null
} = {}) => {
  const assertNotCancelled = () => {
    if (typeof isCancelled === 'function' && isCancelled()) {
      const err = new Error('QR label generation cancelled');
      err.name = 'AbortError';
      throw err;
    }
  };

  const qrBatchRenderer = document.getElementById('qr-batch-renderer');
  if (!qrBatchRenderer) {
    throw new Error('QRBatchRenderer not found in DOM. Make sure it has been rendered.');
  }

  const cardsArray = Array.from(qrBatchRenderer.querySelectorAll('.qr-batch-card'));
  if (expectedCount != null && cardsArray.length !== expectedCount) {
    console.warn(`⚠️ Expected ${expectedCount} cards but found ${cardsArray.length}`);
  }

  const screenDPI = 96;
  const dpiScaleFactor = targetDPI / screenDPI;
  const imageFormat = 'JPEG';
  const jpegQuality = 0.95;
  const cardImages = [];

  for (let idx = 0; idx < cardsArray.length; idx++) {
    assertNotCancelled();
    const card = cardsArray[idx];
    try {
      const canvas = await html2canvas(card, {
        backgroundColor: '#ffffff',
        scale: dpiScaleFactor,
        logging: false,
        useCORS: false,
        allowTaint: true,
        imageTimeout: 0
      });
      assertNotCancelled();
      cardImages.push({
        dataUrl: canvas.toDataURL('image/jpeg', jpegQuality),
        width: canvas.width,
        height: canvas.height,
        format: imageFormat
      });
      if (onCardCaptured) {
        onCardCaptured(idx);
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw err;
      }
      console.error(`Failed to capture card ${idx}:`, err);
      cardImages.push(null);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  return cardImages;
};

/**
 * Build the QR Labels PDF from already-captured card images.
 */
export const buildQRLabelsPDF = async (
  cardImages,
  bulkQRTokens,
  {
    onProgress = null,
    orgName = null,
    locationName = null,
    isCancelled = null
  } = {}
) => {
  if (!bulkQRTokens || bulkQRTokens.length === 0) {
    throw new Error('No QR codes to generate PDF');
  }

  const assertNotCancelled = () => {
    if (typeof isCancelled === 'function' && isCancelled()) {
      const err = new Error('QR label generation cancelled');
      err.name = 'AbortError';
      throw err;
    }
  };

  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 12;
  const marginLeft = 10;
  const marginRight = 10;
  const marginBottom = 12;
  const cols = 4;
  const rows = 5;
  const cellsPerPage = cols * rows;
  const totalPages = Math.ceil(bulkQRTokens.length / cellsPerPage);
  const availableWidth = pageWidth - marginLeft - marginRight;
  const spacing = 2;
  const cellWidth = (availableWidth - (spacing * (cols - 1))) / cols;
  const primaryColor = [102, 126, 234];
  const headerBlockHeight = 20;
  const footerBlockHeight = 6;
  const rowGaps = spacing * (rows - 1);
  const maxCellHeight =
    (pageHeight - marginTop - marginBottom - headerBlockHeight - footerBlockHeight - rowGaps) / rows;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'A4'
  });

  for (let pageNum = 0; pageNum < totalPages; pageNum++) {
    assertNotCancelled();
    if (pageNum > 0) {
      pdf.addPage();
    }

    const pageStartIndex = pageNum * cellsPerPage;
    const pageEndIndex = Math.min(pageStartIndex + cellsPerPage, bulkQRTokens.length);

    let orgLocationText = '';
    if (orgName && locationName) {
      orgLocationText = `${orgName} - ${locationName}`;
    } else if (orgName) {
      orgLocationText = orgName;
    } else if (locationName) {
      orgLocationText = locationName;
    }

    pdf.setFontSize(18);
    pdf.setFont('Open Sans', 'bold');
    pdf.setTextColor(...primaryColor);
    pdf.text('QR Labels', pageWidth / 2, marginTop, { align: 'center' });

    if (orgLocationText) {
      pdf.setFontSize(14);
      pdf.setFont('Open Sans', 'normal');
      pdf.setTextColor(...primaryColor);
      pdf.text(orgLocationText, pageWidth / 2, marginTop + 6.5, { align: 'center' });
    }

    pdf.setFontSize(10);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(140, 140, 140);
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    pdf.text(
      `Page ${pageNum + 1} of ${totalPages}  •  ${dateStr}`,
      pageWidth / 2,
      marginTop + 12,
      { align: 'center' }
    );

    for (let idx = 0; idx < pageEndIndex - pageStartIndex; idx++) {
      const globalIdx = pageStartIndex + idx;
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      const cellX = marginLeft + (col * (cellWidth + spacing));
      const cellY = marginTop + headerBlockHeight + (row * (maxCellHeight + spacing));
      const image = cardImages[globalIdx];

      if (image?.dataUrl) {
        try {
          const aspect = image.height / image.width;
          let drawW = cellWidth;
          let drawH = cellWidth * aspect;
          if (drawH > maxCellHeight) {
            const scale = maxCellHeight / drawH;
            drawW *= scale;
            drawH *= scale;
          }
          pdf.addImage(
            image.dataUrl,
            image.format || 'JPEG',
            cellX + (cellWidth - drawW) / 2,
            cellY,
            drawW,
            drawH
          );
        } catch (error) {
          console.error(`Error adding captured card ${globalIdx}:`, error);
        }
      }

      if (onProgress) {
        onProgress({
          processed: globalIdx + 1,
          total: bulkQRTokens.length,
          currentPage: pageNum + 1,
          totalPages,
          stage: 'layout'
        });
      }
    }
  }

  pdf.setFontSize(6.5);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(140, 140, 140);
  pdf.text(
    'Print in color | 4×5 Grid (20 professional labels per page)',
    pageWidth / 2,
    pageHeight - marginBottom + 4,
    { align: 'center', maxWidth: availableWidth }
  );

  return pdf.output('blob');
};

/**
 * Generate PDF by capturing QRBatchRenderer cards with html2canvas.
 * Prefer chunked rendering from the caller for large batches (see DevicesPage).
 *
 * @param {Array} bulkQRTokens - Array of QR token objects with device info
 * @param {Function} onProgress - Callback to track progress
 * @param {Number} targetDPI - Target DPI for print quality (default: 200)
 * @param {String} errorCorrectionLevel - QR error correction level: 'L'|'M'|'Q'|'H'
 * @param {String} orgName - Organization name for header (optional)
 * @param {String} locationName - Location name for header (optional)
 * @param {Boolean} shouldDownload - If true, automatically downloads the PDF (default: false)
 * @param {Object} options - Optional settings
 * @param {Function} [options.isCancelled] - Return true to abort generation between cards
 * @returns {Promise<Blob>} PDF blob
 */
export const generateQRPDFWithCapture = async (
  bulkQRTokens,
  onProgress = null,
  targetDPI = 200,
  errorCorrectionLevel = 'H',
  orgName = null,
  locationName = null,
  shouldDownload = false,
  options = {}
) => {
  if (!bulkQRTokens || bulkQRTokens.length === 0) {
    throw new Error('No QR codes to generate PDF');
  }

  const { isCancelled } = options;
  const totalStartTime = performance.now();

  const cardImages = await captureQRBatchCards({
    expectedCount: bulkQRTokens.length,
    targetDPI,
    isCancelled,
    onCardCaptured: (idx) => {
      if (onProgress) {
        onProgress({
          processed: idx + 1,
          total: bulkQRTokens.length,
          currentPage: 0,
          totalPages: 0,
          stage: 'capturing'
        });
      }
    }
  });

  const pdfBlob = await buildQRLabelsPDF(cardImages, bulkQRTokens, {
    onProgress,
    orgName,
    locationName,
    isCancelled
  });

  console.log(`✅ PDF ready in ${((performance.now() - totalStartTime) / 1000).toFixed(2)}s (${bulkQRTokens.length} labels)`);

  if (shouldDownload) {
    downloadPDFMobile(pdfBlob, `device-qr-codes-${new Date().toISOString().split('T')[0]}.pdf`);
  }

  return pdfBlob;
};

/**
 * Mobile-friendly PDF download utility
 * Handles both iOS and Android/Desktop download scenarios
 *
 * @param {Blob} pdfBlob - The PDF blob to download
 * @param {String} filename - The desired filename for download
 */
export const downloadPDFMobile = (pdfBlob, filename) => {
  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (isIOS) {
    // iOS: Open in new tab/window (user can save manually via share menu)
    const url = URL.createObjectURL(pdfBlob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      // Don't revoke immediately; let the browser handle it
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else {
      URL.revokeObjectURL(url);
      console.warn('Could not open new window for download');
    }
  } else {
    // Android/Desktop: Standard download
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(pdfBlob);
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(downloadLink.href);
  }
};

/**
 * Helper function to generate high-quality PDF for printing
 * Automatically handles 300 DPI print quality and configurable QR error correction
 *
 * Usage:
 * const pdfBlob = await generatePrintReadyQRPDF(bulkQRTokens, onProgress);
 * // or with custom DPI:
 * const pdfBlob = await generatePrintReadyQRPDF(bulkQRTokens, onProgress, 600);
 * // or with custom error correction:
 * const pdfBlob = await generatePrintReadyQRPDF(bulkQRTokens, onProgress, 300, 'M');
 * // or with auto-download:
 * const pdfBlob = await generatePrintReadyQRPDF(bulkQRTokens, onProgress, 300, 'H', true);
 *
 * @param {Array} bulkQRTokens - Array of QR token objects
 * @param {Function} onProgress - Progress callback (optional)
 * @param {Number} dpi - Target DPI (default: 300 for standard printing)
 * @param {String} errorCorrectionLevel - Error correction level: 'L'|'M'|'Q'|'H' (default: 'H')
 * @param {Boolean} shouldDownload - If true, automatically downloads the PDF (default: false)
 * @returns {Promise<Blob>} Print-ready PDF blob
 */
export const generatePrintReadyQRPDF = async (bulkQRTokens, onProgress = null, dpi = 300, errorCorrectionLevel = 'H', shouldDownload = false) => {
  return generateQRPDFWithCapture(bulkQRTokens, onProgress, dpi, errorCorrectionLevel, null, null, shouldDownload);
};

/**
 * DPI Presets for different print scenarios
 */
export const DPI_PRESETS = {
  SCREEN: 96,          // For screen viewing
  STANDARD: 300,       // Standard professional printing (default)
  HIGH_QUALITY: 600,   // High-quality professional printing
  PHOTO: 1200          // Photo-quality printing
};

/**
 * USAGE GUIDE - Configurable Error Correction & Download Options
 *
 * To use different error correction levels in your PDF generation:
 *
 * 1. In your React component, pass the errorCorrectionLevel prop to QRBatchRenderer:
 *
 *    <QRBatchRenderer
 *      bulkQRTokens={tokens}
 *      isVisible={true}
 *      errorCorrectionLevel="M"  // Choose: 'L' | 'M' | 'Q' | 'H'
 *      onRenderComplete={handleRenderComplete}
 *    />
 *
 * 2. When generating the PDF, pass the same level to the function:
 *
 *    // Option A: Return blob only (for print preview)
 *    const pdfBlob = await generateQRPDFWithCapture(
 *      bulkQRTokens,
 *      onProgress,
 *      300,     // DPI
 *      "M"      // Error Correction Level
 *    );
 *
 *    // Option B: Auto-download the PDF
 *    const pdfBlob = await generateQRPDFWithCapture(
 *      bulkQRTokens,
 *      onProgress,
 *      300,     // DPI
 *      "M",     // Error Correction Level
 *      null,    // orgName (optional)
 *      null,    // locationName (optional)
 *      true     // shouldDownload - triggers auto-download
 *    );
 *
 * 3. Or use the helper function:
 *
 *    // Print preview
 *    const pdfBlob = await generatePrintReadyQRPDF(
 *      bulkQRTokens,
 *      onProgress,
 *      300,     // DPI
 *      "M"      // Error Correction Level
 *    );
 *
 *    // Auto-download
 *    const pdfBlob = await generatePrintReadyQRPDF(
 *      bulkQRTokens,
 *      onProgress,
 *      300,     // DPI
 *      "M",     // Error Correction Level
 *      true     // shouldDownload
 *    );
 *
 * 4. For mobile-friendly downloads, use downloadPDFMobile:
 *
 *    import { downloadPDFMobile } from './pdfGenerator';
 *
 *    downloadPDFMobile(pdfBlob, 'qr-codes.pdf');
 *    // iOS: Opens in new tab (user saves via share menu)
 *    // Android/Desktop: Direct download
 *
 * ERROR CORRECTION LEVEL GUIDE:
 * - 'L': Low (7% redundancy)      - Cleanest, most white space (matches "M" sample)
 * - 'M': Medium (15% redundancy)  - Balanced, good for most uses
 * - 'Q': Quartile (25% redundancy) - Higher robustness
 * - 'H': High (30% redundancy)    - Most robust, very dense (default/current)
 *
 * DOWNLOAD BEHAVIOR:
 * - Desktop/Android: Direct download via browser
 * - iOS: Opens PDF in new tab (user can save via share menu due to browser restrictions)
 *
 * RECOMMENDATION:
 * Use 'M' to match your sample_bulk_qr_labels.pdf document style
 * For mobile users, shouldDownload=true provides best UX
 */


